import { useState } from 'react';

import { KEYBOARD_KEYS } from '@/constants/CommonConstants';
import { AssetTagItem } from '@/pages/Event/Asset/AssetTagItem';
import { SelectOptionProps } from '@/types/CommonTypes';

import { Input } from '@/common';
import { Button } from '@/components/generic';

interface MultiInputProps {
  id: string;
  label: string;
  value: string[];
  handleChange: (value: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  addButtonLabel?: string;
  handleTagRemove?: (tag: string) => void;
}

export const MultiInput = ({
  id,
  label,
  value,
  handleChange,
  placeholder,
  disabled,
  className,
  addButtonLabel,
  handleTagRemove
}: MultiInputProps) => {
  const [inputValue, setInputValue] = useState('');

  const addTag = (tag: string) => {
    const normalized = tag.trim();
    if (!normalized) {
      return;
    }

    if (value.some((v) => v.toLowerCase() === normalized.toLowerCase())) {
      return;
    }

    handleChange([...value, normalized]);
  };

  const removeTag = (tag: string) => {
    handleChange(value.filter((v) => v !== tag));
  };

  const handleEnterKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== KEYBOARD_KEYS.ENTER) {
      return;
    }

    e.preventDefault();
    addTag(inputValue);
    setInputValue('');
  };

  const handleAddClick = () => {
    addTag(inputValue);
    setInputValue('');
  };

  return (
    <div className={className}>
      <div>
        <Input
          id={id}
          value={inputValue}
          type='text'
          label={label}
          disabled={disabled}
          handleChange={(val) => setInputValue(val)}
          onKeyDown={handleEnterKey}
          placeholder={placeholder}
          rightSideIcon={
            <Button
              type='button'
              handleOnClick={handleAddClick}
              disabled={!inputValue.trim()}
              className='!h-8 !min-w-0 !rounded-md !px-3 !text-xs'>
              {addButtonLabel}
            </Button>
          }
        />
      </div>
      {value.length > 0 && (
        <div className='mt-2 flex flex-wrap gap-1'>
          {value.map((tag) => (
            <AssetTagItem
              key={tag}
              tag={{ label: tag, value: tag } as SelectOptionProps}
              handleTagRemove={() => (handleTagRemove ? handleTagRemove(tag) : removeTag(tag))}
              className='!mx-0 !mr-1'
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default MultiInput;
